<template>
  <div v-if="isLoaded">
    <!-- 絶対優先の場合 -->
    <div v-if="qosDetail.qosType === 'ABSOLUTE'">
      <v-table density="compact" class="table-bordered">
        <thead class="text-center">
          <tr>
            <td class="table-light-color">QoS方式</td>
            <td colspan="2">
              {{ $filter.enumTo(qosDetail.qosType, "qosType") }}
            </td>
            <td class="table-light-color" colspan="3">優先度参照先</td>
            <td colspan="4">
              {{
                $filter.enumTo(qosDetail.priorityTarget, "qos_PriorityTarget")
              }}
            </td>
          </tr>
          <tr class="table-light-color">
            <th colspan="2" rowspan="2"></th>
            <th colspan="8" class="text-center table-light-color">
              {{
                $filter.enumTo(qosDetail.priorityTarget, "qos_PriorityTarget")
              }}
            </th>
          </tr>
          <tr>
            <th
              class="text-center table-light-color"
              v-for="index in 8"
              :key="index"
            >
              {{ index - 1 }}
            </th>
          </tr>
        </thead>
        <tbody class="text-center">
          <th :rowspan="5" class="table-light-color">優先度</th>
          <tr
            v-for="(rowName, rowNameIndex) in absolutePriorityRowNames"
            :key="rowNameIndex"
          >
            <th class="text-left table-light-color">{{ rowName.value }}</th>
            <td
              v-for="(propName, propNameIndex) in absolutePriorityPropNames"
              :key="propNameIndex"
            >
              {{
                qosDetail.qosAbsolute &&
                qosDetail.qosAbsolute[propName] === rowName.key
                  ? "●"
                  : "○"
              }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <!-- 帯域優先の場合 -->
    <div v-else-if="qosDetail.qosType === 'BANDWIDTH'">
      <v-table bordered density="compact" class="table-bordered">
        <thead>
          <tr class="text-center">
            <td class="table-light-color">QoS方式</td>
            <td colspan="2">
              {{ $filter.enumTo(qosDetail.qosType, "qosType") }}
            </td>
            <td class="table-light-color" colspan="2">優先度参照先</td>
            <td colspan="">
              {{
                $filter.enumTo(qosDetail.priorityTarget, "qos_PriorityTarget")
              }}
            </td>
          </tr>
          <tr class="table-light-color">
            <th colspan="2" rowspan="2"></th>
            <th colspan="5" class="text-center">
              {{
                $filter.enumTo(qosDetail.priorityTarget, "qos_PriorityTarget")
              }}
            </th>
          </tr>
          <tr v-if="qosDetail.qosBandwidth" class="table-light-color">
            <th class="text-right">
              {{
                $filter.enumTo(
                  qosDetail.qosBandwidth.priorityType1,
                  "qos_PriorityType"
                )
              }}
            </th>
            <th class="text-right">
              {{
                $filter.enumTo(
                  qosDetail.qosBandwidth.priorityType2,
                  "qos_PriorityType"
                )
              }}
            </th>
            <th class="text-right">
              {{
                $filter.enumTo(
                  qosDetail.qosBandwidth.priorityType3,
                  "qos_PriorityType"
                )
              }}
            </th>
            <th class="text-right">ベストエフォート</th>
          </tr>
        </thead>

        <tbody>
          <template
            v-for="(rowName, rowNameIndex) in bandwidthRowNames"
            :key="rowNameIndex"
          >
            <tr
              v-if="!bandControlPattern || rowName.key === bandControlPattern"
            >
              <template v-if="rowNameIndex % 2 === 0">
                <th rowspan="2" class="table-light-color">
                  {{ rowName.value }}
                </th>
                <th class="table-light-color">割当帯域</th>
                <td
                  v-for="(propName, propNameIndex) in bandwidthPropNames"
                  :key="propNameIndex"
                  class="text-right"
                >
                  {{ getBandwidthValue(rowName.key, propName) }}
                </td>
              </template>
              <template v-else>
                <th class="table-light-color">最優先</th>
                <td colspan="4">{{ isType1TopPriority(rowName.key) }}</td>
              </template>
            </tr>
          </template>
        </tbody>
      </v-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { CloudQosInfo } from "@/apis/CloudQosApi";
import type { QosDetail } from "@/apis/QosApi";
import type { ControlPattern } from "@/apis/VNConnectApi";
import type { NonNullableProperty } from "@/shims-vue";

export default defineComponent({
  name: "QosSetting",
  props: {
    /** VPN/VNコード */
    vpnVnCode: {
      type: String as PropType<string>,
      required: true,
    },
    /** QoS帯域制御パターン。指定した場合はそのパターンのみが表示される */
    bandControlPattern: {
      type: String as PropType<ControlPattern | null>,
      required: false,
    },
  },
  data() {
    return {
      isLoaded: false,
      absolutePriorityRowNames: [
        { key: "HIGH", value: "High" },
        { key: "MEDIUM", value: "Medium" },
        { key: "NORMAL", value: "Normal" },
        { key: "LOW", value: "Low" },
      ],
      absolutePriorityPropNames: [
        "priority0",
        "priority1",
        "priority2",
        "priority3",
        "priority4",
        "priority5",
        "priority6",
        "priority7",
      ] as (keyof NonNullableProperty<
        Pick<QosDetail, "qosAbsolute">
      >["qosAbsolute"])[],
      bandwidthRowNames: [
        { key: "PATTERN_A", value: "パターンA" },
        { key: "PATTERN_A", value: "" },
        { key: "PATTERN_B", value: "パターンB" },
        { key: "PATTERN_B", value: "" },
        { key: "PATTERN_C", value: "パターンC" },
        { key: "PATTERN_C", value: "" },
        { key: "PATTERN_D", value: "パターンD" },
        { key: "PATTERN_D", value: "" },
        { key: "PATTERN_E", value: "パターンE" },
        { key: "PATTERN_E", value: "" },
      ],
      bandwidthPropNames: [
        "priorityType1Value",
        "priorityType2Value",
        "priorityType3Value",
        "bestEffort",
      ],
      qosDetail: {} as QosDetail,
      cloudQosSettingInfo: {} as CloudQosInfo,
    };
  },
  async mounted() {
    this.qosDetail = await this.$api.qosApi.getQosDetail(this.vpnVnCode);
    this.isLoaded = true;
  },
  methods: {
    /**
     * 帯域制御方式の値を取得
     * @param patternName パターン名
     * @param propName プロパティ名
     * */
    getBandwidthValue: function (patternName: string, propName: string) {
      const item = this.qosDetail.qosBandwidth?.qosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        if (propName === "bestEffort") {
          return (item.priorityType1Value || 0) +
            (item.priorityType2Value || 0) +
            (item.priorityType3Value || 0)
            ? `${
                100 -
                ((item.priorityType1Value || 0) +
                  (item.priorityType2Value || 0) +
                  (item.priorityType3Value || 0))
              }%`
            : "-";
        } else {
          switch (propName) {
            case "priorityType1Value":
              return item.priorityType1Value
                ? `${item.priorityType1Value}%`
                : "-";
            case "priorityType2Value":
              return item.priorityType2Value
                ? `${item.priorityType2Value}%`
                : "-";
            case "priorityType3Value":
              return item.priorityType3Value
                ? `${item.priorityType3Value}%`
                : "-";
          }
        }
      } else {
        return "-";
      }
    },
    /**
     * 帯域制御方式の最優先設定フラグを取得
     * @param patternName パターン名
     * */
    isType1TopPriority(patternName: string) {
      const item = this.qosDetail.qosBandwidth?.qosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        return item.isType1TopPriority ? "ON" : "OFF";
      } else {
        return "-";
      }
    },
  },
});
</script>

<style lang="scss" scoped>
th {
  font-weight: 700 !important;
  font-size: 16px;
}
td {
  font-size: 16px;
}
.table-light-color {
  background-color: #e9ecef;
}
</style>
